import { Directive, OnInit, ElementRef, inject, input } from '@angular/core';
import { FormGroup, ControlContainer } from '@angular/forms';
import { environment } from '../environments/environment';

@Directive({
  selector: '[appPopulateForm]',
  standalone: true
})
export class PopulateFormDirective implements OnInit {
  private readonly elementRef = inject(ElementRef);
  private readonly controlContainer = inject(ControlContainer, { host: true, optional: true });

  // Input property to receive the form type string (e.g., 'paymentForm')
  // The alias 'appPopulateForm' means in the template you'll write: appPopulateForm="paymentForm"
  public readonly form = input.required<string>({ alias: 'appPopulateForm' });

  private actualFormGroup: FormGroup | null = null;

  async ngOnInit() {
    if (this.controlContainer && this.controlContainer.control && this.controlContainer.control instanceof FormGroup) {
      this.actualFormGroup = this.controlContainer.control;
    } else {
      console.error('PopulateFormDirective: FormGroup not found on the host element via ControlContainer. Ensure the directive is applied to an element that also has a [formGroup] directive.');
      return;
    }

    const formIdentifier = this.form(); // Get the form type from the input

    if (environment.showPopulateButtons && !environment.production && environment.mockData) {
      const mockConfig = environment.mockData[formIdentifier];
      if (mockConfig) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'mat-stroked-button populate-button';
        button.style.marginBottom = '1rem';
        button.innerHTML = '<span class="material-icons" style="vertical-align: middle; margin-right: 4px;">build_circle</span> Load Test Data';

        button.addEventListener('click', async () => {
          if (!this.actualFormGroup) {
            console.error('PopulateFormDirective: FormGroup is not available for patching.');
            return;
          }
          try {
            const module = await import(`../shared/mock/${mockConfig}.mock.ts`);
            let dataToPatch = module.default;
            if (Array.isArray(dataToPatch)) {
              if (dataToPatch.length > 0) {
                dataToPatch = dataToPatch[0];
              } else {
                console.error(`Mock data array loaded from ${mockConfig} is empty.`);
                return;
              }
            }
            if (typeof dataToPatch === 'object' && dataToPatch !== null) {
              this.actualFormGroup.patchValue(dataToPatch);
            } else {
              console.error(`Data loaded from ${mockConfig} is not a suitable object for patching the form:`, dataToPatch);
            }
          } catch (error) {
            console.error(`Error loading mock data for '${formIdentifier}' (file: ${mockConfig}):`, error);
          }
        });

        const hostElement = this.elementRef.nativeElement as HTMLElement;
        if (hostElement.firstChild) {
          hostElement.insertBefore(button, hostElement.firstChild);
        } else {
          hostElement.appendChild(button);
        }
      } else {
        console.warn(`PopulateFormDirective: No mock data configuration found for identifier "${formIdentifier}" in environment.mockData.`);
      }
    }
  }
}
